/**
 * ReviewView - Spaced repetition review session
 *
 * Full review flow:
 * - Fetch due cards from API
 * - 3D flip card display (FlashCard with front/back slots)
 * - SRS rating: Again/Hard/Good/Easy (RatingButtons)
 * - Keyboard shortcuts (Space to flip, 1-4 to rate)
 * - Session complete with stats breakdown
 * - Empty state when no cards due
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { getDueCards, getReviewedTodayCount, submitCardReview, getTagCloud, setCardMastered } from '@/lib/api-client'
import { CardWithSRSSchema } from '@shared/schemas'
import { getImageUrl } from '@/lib/image-utils'
import { useReviewSession, type Rating } from '@/hooks/useReviewSession'
import { useSettings } from '@/hooks/useSettings'
import type { CardWithSRS } from '@shared/schemas'
import { FlashCard } from '@/components/ui/cards'
import { RatingButtons } from '@/components/ui/feedback/RatingButtons'
import { useTopBarContent } from '@/contexts/TopBarContent'
import { speak, getEnglishText } from '@/lib/tts'
import { PaginatedTagCloud } from '@/components/ui/tags'

// Local UI state
type LocalSessionState = 'select' | 'loading' | 'ready' | 'flipped' | 'complete' | 'error'

// Review direction
type ReviewDirection = 'forward' | 'reverse'

// Map CardWithSRS to hook's Card type
const mapToHookCard = (card: CardWithSRS) => ({
  id: String(card.id),
  front: card.front,
  back: card.back,
  interval: card.current_interval_days || 1,
  easeFactor: card.current_ease_factor || 2.5,
})

/** Front face: word + "Tap to flip" hint */
function CardFront({ word, isReverse }: { word: string; isReverse: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center h-full p-6 gap-4">
      {/* Direction badge */}
      {isReverse && (
        <div className="absolute top-5 left-1/2 -translate-x-1/2 flex items-center justify-center px-3 py-1 bg-primary-container/15 rounded-full">
          <span className="text-[10px] font-black text-primary uppercase leading-tight">Production</span>
        </div>
      )}
      {!isReverse && (
        <div className="absolute top-5 left-1/2 -translate-x-1/2 flex items-center justify-center px-3 py-1 bg-primary-container/15 rounded-full">
          <span className="text-[10px] font-black text-primary uppercase leading-tight">New Word</span>
        </div>
      )}
      <h2 className="text-4xl font-extrabold text-on-surface mt-4">{word}</h2>
      <div className="absolute bottom-6 text-center animate-bounce opacity-50">
        <span className="material-symbols-outlined text-3xl text-outline">touch_app</span>
        <p className="text-[10px] font-black uppercase tracking-widest text-outline mt-1">Tap to flip</p>
      </div>
    </div>
  )
}

/** Back face: shows full card details. In reverse mode, highlights the English word as the "answer" */
function CardBack({ card, isReverse }: {
  card: CardWithSRS
  isReverse: boolean
}) {
  const imageUrl = getImageUrl(card.image_key)

  return (
    <div className="flex flex-col h-full overflow-y-auto p-4">
      {/* Image */}
      {imageUrl && (
        <img
          src={imageUrl}
          alt={card.front}
          className="w-full max-h-28 object-contain rounded-2xl mb-3 shrink-0 bg-surface-container-low"
        />
      )}

      {/* In reverse mode, show the English word prominently as the "answer" */}
      {isReverse && (
        <div className="bg-primary/10 rounded-2xl px-4 py-2 mb-2">
          <p className="text-xs text-primary font-bold uppercase mb-0.5">Answer</p>
          <p className="font-headline font-bold text-xl text-primary">{card.front}</p>
        </div>
      )}

      {/* Word (always show English word for reference) */}
      {!isReverse && (
        <p className="font-headline font-bold text-lg text-on-surface">{card.front}</p>
      )}

      {/* Phonetics */}
      {(card.phonetic_us || card.phonetic_uk) && (
        <div className="flex items-center gap-3 mt-1">
          {card.phonetic_us && (
            <span className="text-xs text-on-surface-variant flex items-center gap-1">
              US {card.phonetic_us}
              <button
                onClick={(e) => { e.stopPropagation(); speak(getEnglishText(card.front, card.back), 'us') }}
                className="material-symbols-outlined text-[16px] text-primary cursor-pointer"
              >
                volume_up
              </button>
            </span>
          )}
          {card.phonetic_uk && (
            <span className="text-xs text-on-surface-variant flex items-center gap-1">
              UK {card.phonetic_uk}
              <button
                onClick={(e) => { e.stopPropagation(); speak(getEnglishText(card.front, card.back), 'uk') }}
                className="material-symbols-outlined text-[16px] text-primary cursor-pointer"
              >
                volume_up
              </button>
            </span>
          )}
        </div>
      )}

      {/* Translation (show Chinese meaning) */}
      <p className="text-sm text-on-surface mt-1">{card.back}</p>

      {/* Details */}
      {(card.definition || card.example) && (
        <div className="mt-2 pt-2">
          {card.pos && (
            <span className="inline-block px-2 py-0.5 bg-surface-container-low rounded-full text-xs font-bold text-on-surface-variant mb-1">
              {card.pos}
            </span>
          )}
          {card.definition && (
            <p className="text-xs text-on-surface-variant">{card.definition}</p>
          )}
          {card.example && (
            <p className="text-xs text-on-surface-variant italic mt-1">&ldquo;{card.example}&rdquo;</p>
          )}
        </div>
      )}

      {/* Actions row */}
      <div className="flex items-center gap-2 mt-3">
        <a
          href={`https://www.google.com/search?q=${encodeURIComponent(card.front)}&tbm=isch`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold text-on-surface-variant hover:text-primary hover:bg-primary/10 transition-colors"
          aria-label={`Search images for ${card.front}`}
        >
          <span className="material-symbols-outlined text-[16px]">image_search</span>
          Images
        </a>
      </div>
    </div>
  )
}

export function ReviewView() {
  const [localSessionState, setLocalSessionState] = useState<LocalSessionState>('select')
  const [error, setError] = useState<string | null>(null)
  const [isFlipped, setIsFlipped] = useState(false)
  const [originalCards, setOriginalCards] = useState<CardWithSRS[]>([])
  const [reviewedToday, setReviewedToday] = useState<number>(0)
  const [reviewDirection, setReviewDirection] = useState<ReviewDirection>('forward')
  const [selectedTagName, setSelectedTagName] = useState('')
  const [resolvedTags, setResolvedTags] = useState<string[]>([])
  const [matchMode, setMatchMode] = useState<'AND' | 'OR' | 'none'>('AND')
  const [masteringCardId, setMasteringCardId] = useState<string | null>(null)
  const [tagCloud, setTagCloud] = useState<{
    domains: Array<{ name: string; count: number }>
    topics: Array<{ name: string; count: number }>
    forms: Array<{ name: string; count: number }>
  } | null>(null)
  const didInitSessionRef = useRef(false)
  const navigate = useNavigate()
  const { setTopBarContent } = useTopBarContent()
  const { maxCardsPerReview } = useSettings()

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Fetch due cards
  const fetchDueCards = useCallback(async (tags?: string[]) => {
    setLocalSessionState('loading')
    setError(null)
    didInitSessionRef.current = false

    try {
      const response = await getDueCards(maxCardsPerReview, reviewDirection, tags)

      if (!response.cards || response.cards.length === 0) {
        setLocalSessionState('complete')
        setOriginalCards([])
        return
      }

      setResolvedTags(response.resolvedTags || [])
      setMatchMode(response.matchMode ?? 'AND')

      const validatedCards = response.cards
        .map(card => {
          const result = CardWithSRSSchema.safeParse(card)
          return result.success ? result.data : null
        })
        .filter((c): c is CardWithSRS => c !== null)

      setOriginalCards(validatedCards)
      setIsFlipped(false)
      await Promise.resolve()
      setLocalSessionState('ready')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cards')
      setLocalSessionState('error')
    }
  }, [reviewDirection, maxCardsPerReview])

  // Handle card rated - submit to API
  const handleCardRated = useCallback(async (card: { id: string }, rating: Rating) => {
    try {
      const ratingMap: Record<Rating, number> = { again: 1, hard: 2, good: 3, easy: 4 }
      await submitCardReview(card.id, ratingMap[rating], reviewDirection)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit review')
    }
  }, [reviewDirection])

  const {
    sessionState: hookSessionState,
    currentCard: hookCurrentCard,
    progress,
    statistics,
    startSession,
    submitRating: hookSubmitRating,
    resetSession,
  } = useReviewSession({
    shuffle: false,
    autoStart: false,
    onCardRated: handleCardRated,
  })

  // Start hook session when cards are loaded
  useEffect(() => {
    if (originalCards.length > 0 && !didInitSessionRef.current && localSessionState === 'ready') {
      const hookCards = originalCards.map(mapToHookCard)
      startSession(hookCards)
      didInitSessionRef.current = true
    }
  }, [originalCards, localSessionState, startSession])

  // Actions
  const fetchReviewedToday = useCallback(() => {
    getReviewedTodayCount()
      .then(data => {
        if (data.success) setReviewedToday(data.reviewedToday)
      })
      .catch((err) => {
        console.warn('Failed to fetch reviewed today count:', err)
      })
  }, [])

  // Fetch tag cloud on mount and when direction changes
  useEffect(() => {
    getTagCloud(reviewDirection)
      .then(data => {
        if (data.success) {
          setTagCloud({
            domains: data.domains,
            topics: data.topics,
            forms: data.forms,
          })
        }
      })
      .catch(() => {
        // Silently fail — tag chips are optional enhancement
      })
  }, [reviewDirection])

  // Start review with a specific tag
  const startReviewWithTag = useCallback((tagPrefix: string, tagName: string) => {
    setSelectedTagName(tagName)
    fetchDueCards([`${tagPrefix}:${tagName}`])
  }, [fetchDueCards])

  // Map hook state to local state
  useEffect(() => {
    if (hookSessionState === 'completed') {
      setLocalSessionState('complete')
      fetchReviewedToday()
    }
  }, [hookSessionState, fetchReviewedToday])

  // Find original card for display (with all metadata)
  const currentCard = hookCurrentCard
    ? originalCards.find(c => String(c.id) === hookCurrentCard.id)
    : null

  const startNewSession = useCallback(() => {
    resetSession()
    setIsFlipped(false)
    setLocalSessionState('select')
    setError(null)
    setSelectedTagName('')
    setResolvedTags([])
    setMatchMode('AND')
    setReviewDirection('forward')
    didInitSessionRef.current = false
    fetchReviewedToday()
  }, [resetSession, fetchReviewedToday])

  const flipCard = useCallback(() => {
    if (localSessionState === 'ready') {
      setIsFlipped(true)
      setLocalSessionState('flipped')
    }
  }, [localSessionState])

  const submitRating = useCallback(async (rating: Rating) => {
    if (localSessionState !== 'flipped') return
    if (!hookCurrentCard) return

    setIsFlipped(false)
    setLocalSessionState('ready')
    await hookSubmitRating(rating)
  }, [localSessionState, hookCurrentCard, hookSubmitRating])

  const handleMastered = useCallback(async () => {
    if (!hookCurrentCard || masteringCardId) return
    setMasteringCardId(hookCurrentCard.id)
    try {
      await setCardMastered(hookCurrentCard.id, true)
      // Skip this card — rate as "easy" and move on
      await submitRating('easy')
    } catch {
      // Silently fail — user can try again
    } finally {
      setMasteringCardId(null)
    }
  }, [hookCurrentCard, masteringCardId, submitRating])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (localSessionState === 'ready' && e.key === ' ') {
        e.preventDefault()
        flipCard()
      } else if (localSessionState === 'flipped') {
        if (e.key === '1') submitRating('again')
        else if (e.key === '2') submitRating('hard')
        else if (e.key === '3') submitRating('good')
        else if (e.key === '4') submitRating('easy')
        else if (e.key === '5') handleMastered()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [localSessionState, flipCard, submitRating, handleMastered])

  // Fetch cards on mount — start with select mode, no auto-fetch

  // Fetch reviewed today count on mount
  useEffect(() => {
    fetchReviewedToday()
  }, [fetchReviewedToday])

  // Progress
  const progressPercent = progress.total > 0
    ? ((progress.reviewed + 1) / progress.total) * 100
    : 0

  // Inject progress indicator into top bar
  useEffect(() => {
    if ((localSessionState === 'ready' || localSessionState === 'flipped') && progress.total > 0) {
      setTopBarContent(
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1 bg-surface-container rounded-full overflow-hidden">
            <div
              className="bg-primary h-full rounded-full transition-all duration-300"
              role="progressbar"
              style={{ width: `${progressPercent}%` }}
              aria-valuenow={progress.reviewed}
              aria-valuemin={0}
              aria-valuemax={progress.total}
              aria-valuetext={`Card ${progress.reviewed + 1} of ${progress.total}`}
            />
          </div>
          <span className="text-[10px] font-black uppercase text-on-surface-variant whitespace-nowrap">
            Card {progress.reviewed + 1} of {progress.total}
          </span>
        </div>
      )
    } else {
      setTopBarContent(null)
    }
  }, [localSessionState, progressPercent, progress.reviewed, progress.total, setTopBarContent])

  // ---- Render states ----

  // Select mode — choose what to review
  if (localSessionState === 'select') {
    const hasTags = tagCloud && (
      tagCloud.topics.length > 0 || tagCloud.domains.length > 0 || tagCloud.forms.length > 0
    )

    return (
      <div className="flex flex-col items-center px-5 pt-5 pb-6">
        {/* Header: icon + title inline, reviewed today badge */}
        <div className="flex items-center gap-3 mb-3">
          <span className="material-symbols-outlined text-4xl text-primary">school</span>
          <div>
            <h2 className="font-headline font-bold text-lg text-on-surface leading-tight">Ready to Review?</h2>
            <p className="text-on-surface-variant text-xs">Pick a topic or start with all due cards</p>
          </div>
          {reviewedToday > 0 && (
            <div className="flex items-center gap-1 bg-primary-container/20 px-2.5 py-1 rounded-full ml-auto">
              <span className="material-symbols-outlined text-primary text-xs">check_circle</span>
              <span className="text-xs font-bold text-primary">{reviewedToday}</span>
            </div>
          )}
        </div>

        {/* Direction toggle — compact pill style */}
        <div className="w-full max-w-sm mb-3">
          <div className="flex rounded-2xl bg-surface-container-low p-0.5">
            <button
              onClick={() => setReviewDirection('forward')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl transition-all cursor-pointer ${
                reviewDirection === 'forward'
                  ? 'bg-primary text-on-primary shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              <span className="text-sm font-bold">Recognition</span>
              <span className={`text-[10px] ${reviewDirection === 'forward' ? 'text-on-primary/70' : 'text-on-surface-variant/60'}`}>
                EN→CN
              </span>
            </button>
            <button
              onClick={() => setReviewDirection('reverse')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl transition-all cursor-pointer ${
                reviewDirection === 'reverse'
                  ? 'bg-primary text-on-primary shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              <span className="text-sm font-bold">Production</span>
              <span className={`text-[10px] ${reviewDirection === 'reverse' ? 'text-on-primary/70' : 'text-on-surface-variant/60'}`}>
                CN→EN
              </span>
            </button>
          </div>
        </div>

        {/* Tag chips — browseable tag cloud */}
        <div className="w-full max-w-sm mb-3 space-y-2">
          {tagCloud === null ? (
            <div className="animate-pulse space-y-2">
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={`skeleton-${i}`}
                    className="h-[28px] rounded-full bg-surface-container-low"
                    style={{ width: `${48 + (i % 3) * 20}px` }}
                  />
                ))}
              </div>
            </div>
          ) : hasTags ? (
            <>
              <PaginatedTagCloud
                key="topics"
                tags={tagCloud.topics}
                label="Topics"
                tagPrefix="topic"
                onTagSelect={startReviewWithTag}
                variant="primary"
              />
              <PaginatedTagCloud
                key="domains"
                tags={tagCloud.domains}
                label="Domains"
                tagPrefix="domain"
                onTagSelect={startReviewWithTag}
                variant="neutral"
              />
            </>
          ) : null}
        </div>

        {/* Start button */}
        <button
          onClick={() => fetchDueCards()}
          className="w-full max-w-sm px-6 py-3 bg-primary text-on-primary rounded-full font-bold shadow-card"
        >
          Start Review
        </button>
      </div>
    )
  }

  // Error
  if (localSessionState === 'error') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
        <span className="material-symbols-outlined text-5xl text-error mb-4">error</span>
        <p className="text-on-surface text-center mb-4">{error || 'Failed to load cards'}</p>
        <button
          onClick={() => fetchDueCards()}
          className="px-6 py-3 bg-primary text-on-primary rounded-full font-semibold"
        >
          Retry
        </button>
      </div>
    )
  }

  // Loading
  if (localSessionState === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <span className="material-symbols-outlined text-5xl text-primary animate-spin">progress_activity</span>
          <p className="text-on-surface-variant mt-4">Loading cards...</p>
        </div>
      </div>
    )
  }

  // Empty state (no cards due)
  if (localSessionState === 'complete' && originalCards.length === 0) {
    const prefillText = selectedTagName
      ? `more words and expressions about ${selectedTagName}`
      : ''

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
        {reviewedToday > 0 && (
          <div className="flex items-center gap-1.5 bg-primary-container/20 px-4 py-2 rounded-full mb-6">
            <span className="material-symbols-outlined text-primary text-sm">check_circle</span>
            <span className="text-sm font-bold text-primary">{reviewedToday} card{reviewedToday !== 1 ? 's' : ''} reviewed today</span>
          </div>
        )}

        <span className="material-symbols-outlined text-6xl text-primary mb-4">celebration</span>
        <h2 className="font-headline font-bold text-xl text-on-surface mb-2">All caught up!</h2>
        <p className="text-on-surface-variant text-center mb-6">
          Nothing to review right now.
        </p>
        <button
          onClick={() => navigate('/', { state: { prefillText } })}
          className="w-full max-w-sm px-6 py-3 bg-primary text-on-primary rounded-full font-bold shadow-card"
        >
          <span className="flex items-center justify-center gap-2">
            <span className="material-symbols-outlined text-[18px]">auto_awesome</span>
            {selectedTagName ? `Make more "${selectedTagName}" cards` : 'Make some cards'}
          </span>
        </button>
        <button
          onClick={startNewSession}
          className="mt-3 text-sm text-on-surface-variant hover:text-on-surface transition-colors"
        >
          Back to review
        </button>
      </div>
    )
  }

  // Session complete with statistics
  if (localSessionState === 'complete' && progress.reviewed > 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
        <span className="material-symbols-outlined text-6xl text-primary mb-4">celebration</span>
        <h2 className="font-headline font-bold text-2xl text-on-surface mb-6">Session Complete!</h2>

        {/* Stats */}
        <div className="w-full max-w-sm bg-surface-container-lowest rounded-2xl p-6 mb-6 shadow-card">
          <div className="text-center mb-4">
            <p className="text-on-surface-variant text-sm">Cards Reviewed</p>
            <p className="font-headline font-bold text-3xl text-primary">{progress.reviewed}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-error-container rounded-2xl p-3 text-center">
              <p className="text-xs font-bold text-on-error-container">Again</p>
              <p className="font-bold text-lg text-on-error-container">{statistics.again}</p>
            </div>
            <div className="bg-accent-amber/20 rounded-2xl p-3 text-center">
              <p className="text-xs font-bold text-accent-amber">Hard</p>
              <p className="font-bold text-lg text-accent-amber">{statistics.hard}</p>
            </div>
            <div className="bg-tertiary-container rounded-2xl p-3 text-center">
              <p className="text-xs font-bold text-on-tertiary-container">Good</p>
              <p className="font-bold text-lg text-on-tertiary-container">{statistics.good}</p>
            </div>
            <div className="bg-primary-container/15 rounded-2xl p-3 text-center">
              <p className="text-xs font-bold text-primary">Easy</p>
              <p className="font-bold text-lg text-primary">{statistics.easy}</p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-on-surface-variant text-sm">Time Spent</p>
            <p className="font-headline font-bold text-xl text-on-surface">{formatTime(statistics.duration)}</p>
          </div>
        </div>

        {/* Today's progress */}
        {reviewedToday > 0 && (
          <div className="flex items-center gap-1.5 bg-primary-container/20 px-4 py-2 rounded-full mb-6">
            <span className="material-symbols-outlined text-primary text-sm">check_circle</span>
            <span className="text-sm font-bold text-primary">{reviewedToday} card{reviewedToday !== 1 ? 's' : ''} reviewed today</span>
          </div>
        )}

        <button
          onClick={startNewSession}
          className="px-8 py-3 bg-primary text-on-primary rounded-full font-bold shadow-card"
        >
          Start New Session
        </button>
      </div>
    )
  }

  // Active session
  if (!currentCard) return null

  return (
    <div className="flex flex-col h-full px-4 py-3 gap-3">
      {/* Match mode feedback */}
      {resolvedTags.length > 0 && matchMode !== 'AND' && (
        <div className="flex items-center gap-1.5 bg-primary-container/10 px-3 py-1.5 rounded-full">
          <span className="material-symbols-outlined text-on-surface-variant text-sm">info</span>
          <span className="text-xs text-on-surface-variant">
            {matchMode === 'none'
              ? `No cards matched "${selectedTagName}" — here's everything due`
              : 'Including cards that partially match your topic'}
          </span>
        </div>
      )}

      {/* Flash card */}
      <div className="flex-1 flex items-center justify-center min-h-0">
        <div className="w-full max-w-sm max-h-full" data-testid="flashcard">
          <FlashCard
            key={currentCard.id}
            front={<CardFront word={reviewDirection === 'reverse' ? currentCard.back : currentCard.front} isReverse={reviewDirection === 'reverse'} />}
            back={<CardBack card={currentCard} isReverse={reviewDirection === 'reverse'} />}
            isFlipped={isFlipped}
            onFlip={flipCard}
          />
        </div>
      </div>

      {/* Rating buttons — sticky so always visible even if card is tall */}
      {isFlipped && (
        <div className="sticky bottom-0 pt-3 pb-2 bg-surface animate-slide-up">
          <RatingButtons onRate={submitRating} onMastered={handleMastered} mastering={masteringCardId === currentCard?.id} />
        </div>
      )}

      {/* Keyboard hint */}
      {!isFlipped && (
        <p className="text-center text-xs text-on-surface-variant pb-2">
          Tap card or press Space to flip
        </p>
      )}
    </div>
  )
}
